<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import schedulingPagesAPI from 'dashboard/api/schedulingPages';
import googleCalendarAPI from 'dashboard/api/googleCalendar';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

// Must match backend SlotService WEEKDAY_KEYS.
const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DEFAULT_WORK_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri'];
// Duração da reunião e o respiro que vem depois dela. Pedido de cliente:
// reunião de 1h com 30 min livres, pra não emendar uma call na outra.
const DURATION_OPTIONS = [15, 20, 30, 45, 60, 75, 90, 120];
const BUFFER_OPTIONS = [0, 5, 10, 15, 20, 30, 45, 60];
const TIMEZONE_OPTIONS = [
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Cuiaba',
  'America/Belem',
  'America/Fortaleza',
  'America/Recife',
  'America/Bahia',
  'America/Rio_Branco',
  'America/Noronha',
];

const isLoading = ref(true);
const isSaving = ref(false);
const pages = ref([]);
const showForm = ref(false);
const calendarConnected = ref(true);
const accountId = computed(() => getters.getCurrentAccountId.value);

const defaultAvailability = () =>
  WEEKDAYS.map(day => ({
    day,
    windows: DEFAULT_WORK_DAYS.includes(day)
      ? [{ start: '09:00', end: '18:00' }]
      : [],
  }));

const blankForm = () => ({
  id: null,
  slug: '',
  title: '',
  description: '',
  duration_minutes: 30,
  duration_options: [],
  services_enabled: false,
  services: [],
  team_user_ids: [],
  buffer_minutes: 0,
  timezone: 'America/Sao_Paulo',
  funnel_id: '',
  stage_name: '',
  min_notice_hours: 2,
  max_advance_days: 30,
  enabled: true,
  block_brazil_holidays: false,
  availability: defaultAvailability(),
});

const form = ref(blankForm());

const funnels = computed(() => getters['funnels/getFunnels'].value);

const formStages = computed(() => {
  if (!form.value.funnel_id) return [];
  return getters['funnels/getFunnelStages'].value(form.value.funnel_id);
});

const funnelLabel = funnelId => {
  const funnel = funnels.value.find(item => item.id === funnelId);
  return funnel ? funnel.name : '';
};

// "90" vira "1h30" — número de minutos cru obriga o leitor a fazer conta.
const formatMinutes = total => {
  const minutes = Number(total) || 0;
  if (minutes < 60)
    return t('INTEGRATION_SETTINGS.SCHEDULING.MIN', { count: minutes });
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest
    ? t('INTEGRATION_SETTINGS.SCHEDULING.HOUR_MIN', { hours, minutes: rest })
    : t('INTEGRATION_SETTINGS.SCHEDULING.HOUR', { count: hours });
};

// Quais dias da semana têm alguma janela aberta. Na lista isso responde de
// relance a pergunta que hoje exige abrir o formulário: "quando essa página
// aceita reunião?".
const openDays = source => {
  const availability = source || {};
  return WEEKDAYS.map(day => ({
    day,
    open: Boolean((availability[day] || []).length),
  }));
};

const formOpenDays = computed(() =>
  form.value.availability.map(entry => ({
    day: entry.day,
    open: entry.windows.some(w => w.start && w.end),
  }))
);

// A prévia do ritmo: em vez de dois números soltos, mostra os horários que o
// cliente vai ver de fato. Lista TODOS, e de todas as janelas do dia — quem tem
// manhã e tarde precisa ver as duas, senão a prévia mente por omissão.
const previewDay = computed(() =>
  form.value.availability.find(entry =>
    entry.windows.some(w => w.start && w.end)
  )
);

const rhythmPreview = computed(() => {
  const day = previewDay.value;
  if (!day) return [];

  const duration = Number(form.value.duration_minutes) || 0;
  const step = duration + (Number(form.value.buffer_minutes) || 0);
  if (step <= 0) return [];

  const toMinutes = value => {
    const [hours, minutes] = value.split(':').map(Number);
    return hours * 60 + minutes;
  };

  return day.windows
    .filter(w => w.start && w.end)
    .flatMap(w => {
      const end = toMinutes(w.end);
      const times = [];
      let cursor = toMinutes(w.start);
      while (cursor + duration <= end) {
        times.push(
          `${String(Math.floor(cursor / 60)).padStart(2, '0')}:${String(
            cursor % 60
          ).padStart(2, '0')}`
        );
        cursor += step;
      }
      return times;
    });
});

const linkFor = page =>
  page?.public_url ||
  (page?.slug ? `${window.location.origin}/agendar/${page.slug}` : '');

const publicLink = computed(() => linkFor(form.value));

const weekdayLabel = day =>
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  t(`INTEGRATION_SETTINGS.SCHEDULING.WEEKDAYS.${day}`);

const loadPages = async () => {
  try {
    const { data } = await schedulingPagesAPI.get();
    pages.value = data.payload || [];
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.SCHEDULING.API.LOAD_ERROR'));
  }
};

const loadCalendarStatus = async () => {
  try {
    const { data } = await googleCalendarAPI.getStatus();
    calendarConnected.value = Boolean(data?.connected);
  } catch (error) {
    // If status fails, surface the informational note rather than hiding it.
    calendarConnected.value = false;
  }
};

onMounted(async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      store.dispatch('funnels/get'),
      store.dispatch('agents/get'),
      loadPages(),
      loadCalendarStatus(),
    ]);
  } finally {
    isLoading.value = false;
  }
});

const onSelectFunnel = () => {
  const validStage = formStages.value.some(
    stage => stage.name === form.value.stage_name
  );
  if (!validStage) {
    form.value.stage_name = '';
  }
};

const openCreate = () => {
  form.value = blankForm();
  showForm.value = true;
};

const availabilityToForm = weeklyAvailability => {
  const source = weeklyAvailability || {};
  return WEEKDAYS.map(day => ({
    day,
    windows: (source[day] || []).map(w => ({
      start: w.start || '',
      end: w.end || '',
    })),
  }));
};

const openEdit = page => {
  form.value = {
    id: page.id,
    slug: page.slug || '',
    public_url: page.public_url || '',
    title: page.title || '',
    description: page.description || '',
    duration_minutes: page.duration_minutes || 30,
    services_enabled: Boolean(page.services_enabled),
    services: Array.isArray(page.services)
      ? page.services.map(item => ({ ...item }))
      : [],
    duration_options: Array.isArray(page.duration_options)
      ? [...page.duration_options]
      : [],
    team_user_ids: Array.isArray(page.team_user_ids)
      ? [...page.team_user_ids]
      : [],
    buffer_minutes: page.buffer_minutes ?? 0,
    timezone: page.timezone || 'America/Sao_Paulo',
    funnel_id: page.funnel_id || '',
    stage_name: page.stage_name || '',
    min_notice_hours: page.min_notice_hours ?? 2,
    max_advance_days: page.max_advance_days ?? 30,
    enabled: page.enabled,
    block_brazil_holidays: Boolean(page.block_brazil_holidays),
    availability: availabilityToForm(page.weekly_availability),
  };
  showForm.value = true;
};

const cancelForm = () => {
  showForm.value = false;
  form.value = blankForm();
};

const addWindow = day => {
  const entry = form.value.availability.find(a => a.day === day);
  if (entry) {
    entry.windows.push({ start: '09:00', end: '18:00' });
  }
};

const removeWindow = (day, index) => {
  const entry = form.value.availability.find(a => a.day === day);
  if (entry) {
    entry.windows.splice(index, 1);
  }
};

// Agenda de TIME (Fase 3): membros do rodízio — o link mostra a
// disponibilidade conjunta e quem recebeu reunião há mais tempo atende.
const accountAgents = computed(() => getters['agents/getAgents'].value || []);

const toggleTeamMember = id => {
  const atual = form.value.team_user_ids || [];
  form.value.team_user_ids = atual.includes(id)
    ? atual.filter(m => m !== id)
    : [...atual, id];
};

// Agenda por SERVIÇO (salão, clínica): cada serviço tem a sua duração.
// Desligada, a página segue com a duração única de sempre.
const addService = () => {
  form.value.services = [
    ...(form.value.services || []),
    { name: '', duration_minutes: 30, price_cents: null },
  ];
};
const removeService = index => {
  form.value.services = (form.value.services || []).filter(
    (_, i) => i !== index
  );
};
const servicePriceInReais = servico =>
  servico.price_cents ? (servico.price_cents / 100).toString() : '';
const setServicePrice = (servico, valor) => {
  const numero = Number(String(valor).replace(',', '.'));
  servico.price_cents = numero > 0 ? Math.round(numero * 100) : null;
};

// Durações que o VISITANTE escolhe no link (estilo Calendly 30-60).
const toggleDurationOption = minutes => {
  const atual = form.value.duration_options || [];
  form.value.duration_options = atual.includes(minutes)
    ? atual.filter(m => m !== minutes)
    : [...atual, minutes].sort((a, b) => a - b);
};

const buildPayload = () => {
  const weeklyAvailability = {};
  form.value.availability.forEach(entry => {
    const valid = entry.windows.filter(w => w.start && w.end);
    if (valid.length) {
      weeklyAvailability[entry.day] = valid.map(w => ({
        start: w.start,
        end: w.end,
      }));
    }
  });
  return {
    title: form.value.title,
    description: form.value.description,
    duration_minutes: form.value.duration_minutes,
    duration_options: form.value.duration_options,
    services_enabled: form.value.services_enabled,
    services: (form.value.services || [])
      .filter(s => s.name && Number(s.duration_minutes) > 0)
      .map(s => ({
        name: s.name.trim(),
        duration_minutes: Number(s.duration_minutes),
        price_cents: s.price_cents || null,
      })),
    team_user_ids: form.value.team_user_ids,
    buffer_minutes: Number(form.value.buffer_minutes) || 0,
    timezone: form.value.timezone,
    funnel_id: form.value.funnel_id || null,
    stage_name: form.value.funnel_id ? form.value.stage_name : null,
    min_notice_hours: Number(form.value.min_notice_hours) || 0,
    max_advance_days: Number(form.value.max_advance_days) || 30,
    enabled: form.value.enabled,
    block_brazil_holidays: form.value.block_brazil_holidays,
    weekly_availability: weeklyAvailability,
  };
};

const hasAvailability = computed(() =>
  form.value.availability.some(entry =>
    entry.windows.some(w => w.start && w.end)
  )
);

const isFormValid = computed(
  () => Boolean(form.value.title?.trim()) && hasAvailability.value
);

const savePage = async () => {
  if (!isFormValid.value) {
    useAlert(t('INTEGRATION_SETTINGS.SCHEDULING.API.VALIDATION_ERROR'));
    return;
  }
  isSaving.value = true;
  try {
    const payload = buildPayload();
    if (form.value.id) {
      const { data } = await schedulingPagesAPI.update(form.value.id, payload);
      form.value.slug = data?.slug || form.value.slug;
      form.value.public_url = data?.public_url || form.value.public_url;
      useAlert(t('INTEGRATION_SETTINGS.SCHEDULING.API.UPDATE_SUCCESS'));
    } else {
      const { data } = await schedulingPagesAPI.create(payload);
      form.value.id = data?.id || null;
      form.value.slug = data?.slug || '';
      form.value.public_url = data?.public_url || '';
      useAlert(t('INTEGRATION_SETTINGS.SCHEDULING.API.CREATE_SUCCESS'));
    }
    await loadPages();
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.SCHEDULING.API.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const deletePage = async page => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('INTEGRATION_SETTINGS.SCHEDULING.DELETE_CONFIRM'))) {
    return;
  }
  try {
    await schedulingPagesAPI.delete(page.id);
    useAlert(t('INTEGRATION_SETTINGS.SCHEDULING.API.DELETE_SUCCESS'));
    if (form.value.id === page.id) {
      cancelForm();
    }
    await loadPages();
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.SCHEDULING.API.DELETE_ERROR'));
  }
};

const copyLink = async () => {
  try {
    await copyTextToClipboard(publicLink.value);
    useAlert(t('INTEGRATION_SETTINGS.SCHEDULING.LINK.COPIED'));
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.SCHEDULING.LINK.COPY_ERROR'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('INTEGRATION_SETTINGS.SCHEDULING.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.SCHEDULING.TITLE')"
        :description="$t('INTEGRATION_SETTINGS.SCHEDULING.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6">
        <!-- Intro -->
        <div
          class="bg-n-alpha-2 border border-n-weak rounded-md p-4 text-sm text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.SCHEDULING.INTRO') }}
        </div>

        <!-- Google Calendar warning -->
        <div
          v-if="!calendarConnected"
          class="flex items-start gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4 text-sm text-n-slate-11 shadow-sm"
        >
          <div
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-n-amber-3"
          >
            <span class="i-lucide-calendar-x text-base text-n-amber-11" />
          </div>
          <span class="pt-1.5">
            {{ $t('INTEGRATION_SETTINGS.SCHEDULING.NO_CALENDAR') }}
            <router-link
              class="font-medium text-n-blue-text underline"
              :to="`/app/accounts/${accountId}/settings/integrations/google-calendar`"
            >
              {{ $t('INTEGRATION_SETTINGS.SCHEDULING.CONNECT_CALENDAR') }}
            </router-link>
          </span>
        </div>

        <!-- Pages list -->
        <div v-if="!showForm" class="flex flex-col gap-4">
          <div class="flex items-center justify-between">
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.SCHEDULING.LIST.TITLE') }}
            </h3>
            <Button
              icon="i-lucide-plus"
              :label="$t('INTEGRATION_SETTINGS.SCHEDULING.LIST.ADD')"
              @click="openCreate"
            />
          </div>

          <div
            v-if="!pages.length"
            class="flex flex-col items-center gap-3 rounded-xl border border-dashed border-n-weak px-6 py-12 text-center"
          >
            <div
              class="flex h-12 w-12 items-center justify-center rounded-xl bg-n-teal-3"
            >
              <span class="i-lucide-calendar-clock text-xl text-n-teal-11" />
            </div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.SCHEDULING.LIST.EMPTY') }}
            </p>
            <p class="max-w-sm text-sm text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.SCHEDULING.LIST.EMPTY_HINT') }}
            </p>
            <Button
              icon="i-lucide-plus"
              class="mt-1"
              :label="$t('INTEGRATION_SETTINGS.SCHEDULING.LIST.ADD')"
              @click="openCreate"
            />
          </div>

          <div
            v-for="page in pages"
            :key="page.id"
            class="group flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4 shadow-sm transition-colors hover:border-n-slate-6 sm:flex-row sm:items-start sm:justify-between"
          >
            <div class="flex min-w-0 flex-col gap-2">
              <div class="flex flex-wrap items-center gap-2">
                <span class="font-medium text-n-slate-12">
                  {{ page.title }}
                </span>
                <span
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                  :class="
                    page.enabled
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-alpha-2 text-n-slate-11'
                  "
                >
                  {{
                    page.enabled
                      ? $t('INTEGRATION_SETTINGS.SCHEDULING.LIST.ENABLED')
                      : $t('INTEGRATION_SETTINGS.SCHEDULING.LIST.DISABLED')
                  }}
                </span>
              </div>

              <!-- Ritmo: duração e o respiro que vem depois -->
              <div
                class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-n-slate-11"
              >
                <span class="font-medium tabular-nums text-n-slate-12">
                  {{ formatMinutes(page.duration_minutes) }}
                </span>
                <span v-if="page.buffer_minutes" class="tabular-nums">
                  {{
                    $t('INTEGRATION_SETTINGS.SCHEDULING.LIST.BUFFER', {
                      gap: formatMinutes(page.buffer_minutes),
                    })
                  }}
                </span>
                <span v-if="page.funnel_id">
                  {{ funnelLabel(page.funnel_id) }}
                </span>
              </div>

              <!-- Semana: dias com janela aberta -->
              <div class="flex items-center gap-1">
                <span
                  v-for="entry in openDays(page.weekly_availability)"
                  :key="entry.day"
                  class="flex h-5 w-6 items-center justify-center rounded text-[10px] font-medium uppercase"
                  :class="
                    entry.open
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-alpha-2 text-n-slate-10'
                  "
                  :title="weekdayLabel(entry.day)"
                >
                  {{ weekdayLabel(entry.day).slice(0, 1) }}
                </span>
              </div>

              <a
                v-if="page.slug"
                :href="linkFor(page)"
                class="truncate text-xs text-n-blue-text underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ linkFor(page) }}
              </a>
            </div>

            <div class="flex shrink-0 items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                icon="i-lucide-pencil"
                :label="$t('INTEGRATION_SETTINGS.SCHEDULING.LIST.EDIT')"
                @click="openEdit(page)"
              />
              <Button
                variant="outline"
                color="ruby"
                size="sm"
                icon="i-lucide-trash-2"
                :label="$t('INTEGRATION_SETTINGS.SCHEDULING.LIST.DELETE')"
                @click="deletePage(page)"
              />
            </div>
          </div>
        </div>

        <!-- Form -->
        <form v-else class="flex flex-col gap-4" @submit.prevent="savePage">
          <h3 class="text-base font-semibold text-n-slate-12">
            {{
              form.id
                ? $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.EDIT_TITLE')
                : $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.CREATE_TITLE')
            }}
          </h3>

          <!-- Link público, assim que a página existe -->
          <div
            v-if="form.slug"
            class="flex flex-col gap-2 rounded-xl border border-n-weak bg-n-alpha-2 p-4"
          >
            <span class="text-xs font-medium text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.SCHEDULING.LINK.LABEL') }}
            </span>
            <div class="flex flex-wrap items-center gap-2">
              <input
                :value="publicLink"
                type="text"
                readonly
                class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand flex-1"
              />
              <Button
                type="button"
                variant="faded"
                size="sm"
                icon="i-lucide-copy"
                :label="$t('INTEGRATION_SETTINGS.SCHEDULING.LINK.COPY')"
                @click="copyLink"
              />
              <a
                :href="publicLink"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex h-8 items-center gap-1.5 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
              >
                <span class="i-lucide-external-link text-base" />
                {{ $t('INTEGRATION_SETTINGS.SCHEDULING.LINK.OPEN') }}
              </a>
            </div>
          </div>

          <!-- O que o cliente lê -->
          <div
            class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 shadow-sm"
          >
            <div
              class="flex items-center gap-3 border-b border-n-weak px-5 py-4"
            >
              <div
                class="flex h-8 w-8 items-center justify-center rounded-lg bg-n-blue-3"
              >
                <span class="i-lucide-type text-base text-n-blue-11" />
              </div>
              <h4 class="text-sm font-semibold text-n-slate-12">
                {{ $t('INTEGRATION_SETTINGS.SCHEDULING.SECTIONS.IDENTITY') }}
              </h4>
            </div>
            <div class="flex flex-col gap-4 p-5">
              <label
                class="flex flex-col gap-1.5 text-xs font-medium text-n-slate-11"
              >
                {{ $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.PAGE_TITLE') }}
                <input
                  v-model="form.title"
                  type="text"
                  class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  :placeholder="
                    $t(
                      'INTEGRATION_SETTINGS.SCHEDULING.FORM.PAGE_TITLE_PLACEHOLDER'
                    )
                  "
                />
              </label>
              <label
                class="flex flex-col gap-1.5 text-xs font-medium text-n-slate-11"
              >
                {{ $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.DESCRIPTION') }}
                <textarea
                  v-model="form.description"
                  rows="2"
                  class="w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  :placeholder="
                    $t(
                      'INTEGRATION_SETTINGS.SCHEDULING.FORM.DESCRIPTION_PLACEHOLDER'
                    )
                  "
                />
              </label>
            </div>
          </div>

          <!-- Ritmo: quanto dura e quanto sobra depois -->
          <div
            class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 shadow-sm"
          >
            <div
              class="flex items-center gap-3 border-b border-n-weak px-5 py-4"
            >
              <div
                class="flex h-8 w-8 items-center justify-center rounded-lg bg-n-teal-3"
              >
                <span class="i-lucide-timer text-base text-n-teal-11" />
              </div>
              <div>
                <h4 class="text-sm font-semibold text-n-slate-12">
                  {{ $t('INTEGRATION_SETTINGS.SCHEDULING.SECTIONS.RHYTHM') }}
                </h4>
                <p class="mt-0.5 text-xs text-n-slate-11">
                  {{
                    $t('INTEGRATION_SETTINGS.SCHEDULING.SECTIONS.RHYTHM_HINT')
                  }}
                </p>
              </div>
            </div>
            <div class="p-5">
              <div class="grid gap-4 md:grid-cols-2">
                <label
                  class="flex flex-col gap-1.5 text-xs font-medium text-n-slate-11"
                >
                  {{ $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.DURATION') }}
                  <select
                    v-model.number="form.duration_minutes"
                    class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  >
                    <option
                      v-for="opt in DURATION_OPTIONS"
                      :key="opt"
                      :value="opt"
                    >
                      {{ formatMinutes(opt) }}
                    </option>
                  </select>
                  <span class="mt-1 text-[11px] font-normal text-n-slate-10">
                    {{
                      $t(
                        'INTEGRATION_SETTINGS.SCHEDULING.FORM.DURATION_CHOICES'
                      )
                    }}
                  </span>
                  <label
                    class="mb-2 flex items-center gap-2 text-xs font-normal text-n-slate-11"
                  >
                    <input v-model="form.services_enabled" type="checkbox" />
                    {{ $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.SERVICES_ON') }}
                  </label>
                  <div
                    v-if="form.services_enabled"
                    class="mb-3 flex flex-col gap-2"
                    data-testid="services-editor"
                  >
                    <span class="text-[11px] font-normal text-n-slate-10">
                      {{
                        $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.SERVICES_HINT')
                      }}
                    </span>
                    <div
                      v-for="(servico, index) in form.services || []"
                      :key="`servico-${index}`"
                      class="flex flex-wrap items-center gap-2"
                    >
                      <input
                        v-model="servico.name"
                        type="text"
                        class="form-input flex-1 min-w-[10rem] text-sm"
                        :placeholder="
                          $t(
                            'INTEGRATION_SETTINGS.SCHEDULING.FORM.SERVICE_NAME'
                          )
                        "
                      />
                      <input
                        v-model.number="servico.duration_minutes"
                        type="number"
                        min="5"
                        step="5"
                        class="form-input w-24 text-sm"
                        :placeholder="
                          $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.SERVICE_MIN')
                        "
                      />
                      <input
                        :value="servicePriceInReais(servico)"
                        type="text"
                        class="form-input w-28 text-sm"
                        :placeholder="
                          $t(
                            'INTEGRATION_SETTINGS.SCHEDULING.FORM.SERVICE_PRICE'
                          )
                        "
                        @input="setServicePrice(servico, $event.target.value)"
                      />
                      <button
                        type="button"
                        class="text-xs text-n-ruby-11"
                        @click="removeService(index)"
                      >
                        {{
                          $t(
                            'INTEGRATION_SETTINGS.SCHEDULING.FORM.SERVICE_REMOVE'
                          )
                        }}
                      </button>
                    </div>
                    <button
                      type="button"
                      class="self-start rounded-lg border border-n-weak px-2.5 py-1 text-[11px] text-n-slate-11"
                      @click="addService"
                    >
                      {{
                        $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.SERVICE_ADD')
                      }}
                    </button>
                  </div>
                  <div class="flex flex-wrap gap-1.5">
                    <button
                      v-for="opt in DURATION_OPTIONS"
                      :key="`escolha-${opt}`"
                      type="button"
                      class="rounded-full border px-2.5 py-1 text-[11px]"
                      :class="
                        (form.duration_options || []).includes(opt)
                          ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
                          : 'border-n-weak text-n-slate-11 hover:border-n-slate-8'
                      "
                      @click="toggleDurationOption(opt)"
                    >
                      {{ formatMinutes(opt) }}
                    </button>
                  </div>
                  <span class="mt-2 text-[11px] font-normal text-n-slate-10">
                    {{ $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.TEAM_HINT') }}
                  </span>
                  <div class="flex flex-wrap gap-1.5">
                    <button
                      v-for="agent in accountAgents"
                      :key="`time-${agent.id}`"
                      type="button"
                      class="rounded-full border px-2.5 py-1 text-[11px]"
                      :class="
                        (form.team_user_ids || []).includes(agent.id)
                          ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
                          : 'border-n-weak text-n-slate-11 hover:border-n-slate-8'
                      "
                      @click="toggleTeamMember(agent.id)"
                    >
                      {{ agent.name }}
                    </button>
                  </div>
                </label>
                <label
                  class="flex flex-col gap-1.5 text-xs font-medium text-n-slate-11"
                >
                  {{ $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.BUFFER') }}
                  <select
                    v-model.number="form.buffer_minutes"
                    class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  >
                    <option
                      v-for="opt in BUFFER_OPTIONS"
                      :key="opt"
                      :value="opt"
                    >
                      {{
                        opt
                          ? formatMinutes(opt)
                          : $t(
                              'INTEGRATION_SETTINGS.SCHEDULING.FORM.BUFFER_NONE'
                            )
                      }}
                    </option>
                  </select>
                </label>
              </div>

              <!-- Os horários que o cliente vai ver de fato -->
              <div
                v-if="rhythmPreview.length"
                class="mt-4 rounded-lg bg-n-alpha-2 px-4 py-3"
              >
                <p class="text-xs text-n-slate-11">
                  {{
                    $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.RHYTHM_PREVIEW')
                  }}
                </p>
                <div class="mt-2 flex flex-wrap items-center gap-2">
                  <span
                    v-for="time in rhythmPreview"
                    :key="time"
                    class="rounded-md bg-n-teal-3 px-2 py-1 text-sm font-medium tabular-nums text-n-teal-11"
                  >
                    {{ time }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Quando aceita reunião -->
          <div
            class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 shadow-sm"
          >
            <div
              class="flex items-center gap-3 border-b border-n-weak px-5 py-4"
            >
              <div
                class="flex h-8 w-8 items-center justify-center rounded-lg bg-n-amber-3"
              >
                <span
                  class="i-lucide-calendar-days text-base text-n-amber-11"
                />
              </div>
              <div class="flex-1">
                <h4 class="text-sm font-semibold text-n-slate-12">
                  {{
                    $t('INTEGRATION_SETTINGS.SCHEDULING.SECTIONS.AVAILABILITY')
                  }}
                </h4>
                <p class="mt-0.5 text-xs text-n-slate-11">
                  {{
                    $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.AVAILABILITY_HINT')
                  }}
                </p>
              </div>
              <div class="hidden items-center gap-1 sm:flex">
                <span
                  v-for="entry in formOpenDays"
                  :key="entry.day"
                  class="flex h-5 w-6 items-center justify-center rounded text-[10px] font-medium uppercase"
                  :class="
                    entry.open
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-alpha-2 text-n-slate-10'
                  "
                  :title="weekdayLabel(entry.day)"
                >
                  {{ weekdayLabel(entry.day).slice(0, 1) }}
                </span>
              </div>
            </div>
            <div class="flex flex-col gap-4 p-5">
              <div class="grid gap-4 md:grid-cols-3">
                <label
                  class="flex flex-col gap-1.5 text-xs font-medium text-n-slate-11"
                >
                  {{ $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.TIMEZONE') }}
                  <select
                    v-model="form.timezone"
                    class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  >
                    <option
                      v-for="tz in TIMEZONE_OPTIONS"
                      :key="tz"
                      :value="tz"
                    >
                      {{ tz }}
                    </option>
                  </select>
                </label>
                <label
                  class="flex flex-col gap-1.5 text-xs font-medium text-n-slate-11"
                >
                  {{ $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.MIN_NOTICE') }}
                  <input
                    v-model="form.min_notice_hours"
                    type="number"
                    min="0"
                    class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  />
                </label>
                <label
                  class="flex flex-col gap-1.5 text-xs font-medium text-n-slate-11"
                >
                  {{ $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.MAX_ADVANCE') }}
                  <input
                    v-model="form.max_advance_days"
                    type="number"
                    min="1"
                    class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  />
                </label>
              </div>

              <label
                class="flex cursor-pointer items-start justify-between gap-4 rounded-lg border border-n-weak bg-n-alpha-2 px-4 py-3"
              >
                <span>
                  <span class="block text-sm font-medium text-n-slate-12">
                    {{
                      $t(
                        'INTEGRATION_SETTINGS.SCHEDULING.FORM.BLOCK_BRAZIL_HOLIDAYS'
                      )
                    }}
                  </span>
                  <span class="mt-1 block text-xs text-n-slate-10">
                    {{
                      $t(
                        'INTEGRATION_SETTINGS.SCHEDULING.FORM.BLOCK_BRAZIL_HOLIDAYS_HINT'
                      )
                    }}
                  </span>
                </span>
                <input
                  v-model="form.block_brazil_holidays"
                  type="checkbox"
                  class="mt-0.5 size-4 shrink-0"
                />
              </label>

              <div class="flex flex-col gap-2">
                <div
                  v-for="entry in form.availability"
                  :key="entry.day"
                  class="flex flex-col gap-2 rounded-lg border border-n-weak px-4 py-3"
                >
                  <div class="flex items-center justify-between">
                    <span class="text-sm font-medium text-n-slate-12">
                      {{ weekdayLabel(entry.day) }}
                    </span>
                    <Button
                      type="button"
                      variant="faded"
                      size="xs"
                      icon="i-lucide-plus"
                      :label="
                        $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.ADD_WINDOW')
                      "
                      @click="addWindow(entry.day)"
                    />
                  </div>
                  <span
                    v-if="!entry.windows.length"
                    class="text-xs text-n-slate-10"
                  >
                    {{ $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.UNAVAILABLE') }}
                  </span>
                  <div
                    v-for="(window, index) in entry.windows"
                    :key="index"
                    class="flex items-center gap-2"
                  >
                    <input
                      v-model="window.start"
                      type="time"
                      class="h-10 w-32 rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm tabular-nums text-n-slate-12 outline-none focus:border-n-brand"
                    />
                    <span class="text-xs text-n-slate-11">
                      {{ $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.TO') }}
                    </span>
                    <input
                      v-model="window.end"
                      type="time"
                      class="h-10 w-32 rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm tabular-nums text-n-slate-12 outline-none focus:border-n-brand"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      color="ruby"
                      size="sm"
                      icon="i-lucide-trash-2"
                      @click="removeWindow(entry.day, index)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Destino no funil -->
          <div
            class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 shadow-sm"
          >
            <div
              class="flex items-center gap-3 border-b border-n-weak px-5 py-4"
            >
              <div
                class="flex h-8 w-8 items-center justify-center rounded-lg bg-n-violet-3"
              >
                <span class="i-lucide-git-branch text-base text-n-violet-11" />
              </div>
              <h4 class="text-sm font-semibold text-n-slate-12">
                {{ $t('INTEGRATION_SETTINGS.SCHEDULING.SECTIONS.FUNNEL') }}
              </h4>
            </div>
            <div class="grid gap-4 p-5 md:grid-cols-2">
              <label
                class="flex flex-col gap-1.5 text-xs font-medium text-n-slate-11"
              >
                {{ $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.FUNNEL') }}
                <select
                  v-model="form.funnel_id"
                  class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  @change="onSelectFunnel"
                >
                  <option value="">
                    {{ $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.FUNNEL_NONE') }}
                  </option>
                  <option
                    v-for="funnel in funnels"
                    :key="funnel.id"
                    :value="funnel.id"
                  >
                    {{ funnel.name }}
                  </option>
                </select>
              </label>
              <label
                class="flex flex-col gap-1.5 text-xs font-medium text-n-slate-11"
              >
                {{ $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.STAGE') }}
                <select
                  v-model="form.stage_name"
                  class="h-10 w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  :disabled="!form.funnel_id"
                >
                  <option value="">
                    {{ $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.STAGE_NONE') }}
                  </option>
                  <option
                    v-for="stage in formStages"
                    :key="stage.name"
                    :value="stage.name"
                  >
                    {{ stage.name }}
                  </option>
                </select>
              </label>
            </div>
          </div>

          <div
            class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-solid-2 px-5 py-4 shadow-sm"
          >
            <label class="flex items-center gap-2">
              <input v-model="form.enabled" type="checkbox" />
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('INTEGRATION_SETTINGS.SCHEDULING.FORM.ENABLED') }}
              </span>
            </label>
            <div class="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                icon="i-lucide-arrow-left"
                :label="$t('INTEGRATION_SETTINGS.SCHEDULING.FORM.BACK')"
                @click="cancelForm"
              />
              <Button
                type="submit"
                icon="i-lucide-save"
                :label="$t('INTEGRATION_SETTINGS.SCHEDULING.FORM.SAVE')"
                :is-loading="isSaving"
                :disabled="!isFormValid"
              />
            </div>
          </div>
        </form>
      </div>
    </template>
  </SettingsLayout>
</template>
